import { useState } from "react";
import { Link } from "react-router-dom";
import StarRating from "../../shared/StarRating";
import { useAppContext } from "../../../context";

const TAGS = ["Popular","Chef's Pick","New","Spicy","Must Try","Best Seller","Fan Fav","Limited","Trending","House Special","Crispy","Comfort Food","Signature","Fresh Today","Highly Rated"];

export default function ProductCard ({ product = {}, theme = "light", compact = false })  {
  const [qty, setQty] = useState(1);
  const [tag] = useState(() => TAGS[Math.floor(Math.random() * TAGS.length)]);
  const [rating] = useState(() => Math.round((3.5 + Math.random() * 1.5) * 10) / 10);
  const [ratingCount] = useState(() => Math.floor(10 + Math.random() * 490));
  const { addToCart, toggleWishlist, wishlist } = useAppContext();
  const isWishlisted = wishlist.some(item => item.id === product.id);

  if (compact) return (
    <div className={`group flex items-center gap-3 rounded-2xl border px-3 py-3 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5
      ${theme === "dark" ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>

      {/* Image */}
      <Link to={`/menu/${product.id}`} className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden shadow-md">
        <img src={product?.images?.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${theme === "dark" ? "bg-gray-700 text-orange-400" : "bg-orange-50 text-orange-500"}`}>
            {product?.category?.name}
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 hidden sm:inline">{tag}</span>
        </div>
        <Link to={`/menu/${product.id}`}>
          <h3 className={`font-bold text-sm truncate hover:text-orange-500 transition-colors ${theme === "dark" ? "text-white" : "text-gray-900"}`}>{product.name}</h3>
        </Link>
        <div className="flex items-center gap-1 mt-1">
          <StarRating rating={rating} />
          <span className={`text-[10px] ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>({ratingCount})</span>
        </div>
      </div>

      {/* Price + Actions */}
      <div className="flex flex-col items-end gap-1.5 md:flex-row md:items-center md:gap-3 flex-shrink-0">
        <span className="font-black text-base md:text-xl text-orange-500">{product.price}</span>
        <div className={`hidden md:flex items-center gap-1 rounded-xl px-1 py-1 ${theme === "dark" ? "bg-gray-700" : "bg-gray-100"}`}>
          <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-6 h-6 rounded-lg bg-white text-gray-600 text-xs font-bold shadow-sm hover:bg-orange-50 transition-colors">−</button>
          <span className={`w-5 text-center text-xs font-bold ${theme === "dark" ? "text-white" : "text-gray-800"}`}>{qty}</span>
          <button onClick={() => setQty(qty + 1)} className="w-6 h-6 rounded-lg bg-white text-gray-600 text-xs font-bold shadow-sm hover:bg-orange-50 transition-colors">+</button>
        </div>
        <button onClick={() => addToCart({ ...product, qty })}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 md:px-5 py-1.5 rounded-xl text-xs font-bold shadow-sm hover:shadow-md hover:scale-105 transition-all">
          + Add
        </button>
        <button onClick={() => toggleWishlist(product)}
          className={`hidden md:flex w-8 h-8 rounded-full items-center justify-center transition-all
            ${isWishlisted ? "bg-red-500 text-white scale-110 shadow-md" : "bg-gray-100 text-gray-400 hover:text-red-400 hover:bg-red-50"}`}>
          {isWishlisted ? "♥" : "♡"}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`group rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border
      ${theme === "dark" ? "bg-gray-800 border-gray-700" : "bg-white border-gray-100"}`}>
      <Link to={`/menu/${product.id}`} className="relative block overflow-hidden">
        <img src={product?.images?.image} alt={product.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-3 left-3 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-lg">{tag}</span>
        <button onClick={(e) => { e.preventDefault(); toggleWishlist(product); }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all
            ${isWishlisted ? "bg-red-500 text-white scale-110" : "bg-white/90 text-gray-400 hover:text-red-500"}`}>
          {isWishlisted ? "♥" : "♡"}
        </button>
      </Link>
      <div className="p-4">
        <div className="flex justify-between items-start mb-1">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${theme === "dark" ? "bg-gray-700 text-orange-400" : "bg-orange-50 text-orange-500"}`}>{product?.category?.name}</span>
        </div>
        <Link to={`/menu/${product.id}`}>
          <h3 className={`font-bold text-sm mt-2 mb-1 hover:text-orange-500 transition-colors ${theme === "dark" ? "text-white" : "text-gray-900"}`}>{product.name}</h3>
        </Link>
        <p className={`text-xs mb-2 line-clamp-2 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>{product.description}</p>
        <div className="flex items-center gap-1 mb-3">
          <StarRating rating={rating} />
          <span className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>({ratingCount})</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-black text-lg text-orange-500">{product.price}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-6 h-6 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold hover:bg-orange-100 transition-colors">−</button>
            <span className={`w-6 text-center text-xs font-semibold ${theme === "dark" ? "text-white" : "text-gray-700"}`}>{qty}</span>
            <button onClick={() => setQty(qty + 1)} className="w-6 h-6 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold hover:bg-orange-100 transition-colors">+</button>
          </div>
        </div>
        <button onClick={() => addToCart({ ...product, qty })}
          className="w-full mt-3 bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 rounded-xl text-xs font-bold hover:shadow-lg hover:scale-[1.02] transition-all">
          + Add to Cart
        </button>
      </div>
    </div>
  );
};